import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { getEnv } from '@/lib/env';

const TestPage = () => {
  const [status, setStatus] = useState('Testing...');
  const [details, setDetails] = useState<any[]>([]);

  useEffect(() => {
    const runTests = async () => {
      const testResults: any[] = [];

      // Test 1: Check if Supabase client exists
      testResults.push({
        test: 'Supabase Client',
        result: supabase ? '✅ Connected' : '❌ Not connected',
        details: supabase ? 'Client initialized' : 'Client is null'
      });

      if (!supabase) {
        setDetails(testResults);
        setStatus('❌ Supabase client not available');
        return;
      }

      // Test 2: Check environment variables
      const { supabaseUrl, supabaseAnonKey } = getEnv();

      testResults.push({
        test: 'Environment Variables',
        result: supabaseUrl && supabaseAnonKey ? '✅ Loaded' : '⚠️ Missing',
        details: {
          url: supabaseUrl ? 'Set' : 'Missing',
          key: supabaseAnonKey ? 'Set' : 'Missing'
        }
      });

      // Test 3: Test database connection
      try {
        const { data, error } = await supabase
          .from('app_users')
          .select('count')
          .limit(1);

        testResults.push({
          test: 'Database Connection',
          result: error ? '❌ Failed' : '✅ Connected',
          details: error ? error.message : 'Successfully queried app_users table'
        });

        // Test 4: Check for admin user
        const { data: adminUser, error: adminError } = await supabase
          .from('app_users')
          .select('email, role')
          .eq('email', 'george.mcpherson@rightawaygroup.com')
          .single();

        testResults.push({
          test: 'Admin User in Database',
          result: adminError ? '❌ Not found' : '✅ Found',
          details: adminError ? adminError.message : `Role: ${adminUser?.role}`
        });

      } catch (error: any) {
        testResults.push({
          test: 'Database Connection',
          result: '❌ Failed',
          details: error.message
        });
      }

      // Test 5: Test auth connection
      try {
        const { data: session } = await supabase.auth.getSession();
        testResults.push({
          test: 'Auth Service',
          result: '✅ Available',
          details: session.session ? 'User logged in' : 'No active session'
        });
      } catch (error: any) {
        testResults.push({
          test: 'Auth Service',
          result: '❌ Failed',
          details: error.message
        });
      }

      // Test 6: Test actual login
      try {
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email: 'george.mcpherson@rightawaygroup.com',
          password: 'RahsMatch2024!'
        });

        if (authError) {
          testResults.push({
            test: 'Login Test',
            result: '❌ Failed',
            details: `Auth Error: ${authError.message}`
          });
        } else if (authData.user) {
          testResults.push({
            test: 'Login Test',
            result: '✅ Success',
            details: `User ID: ${authData.user.id}, Email: ${authData.user.email}`
          });
          // Sign out immediately after test
          await supabase.auth.signOut();
        }
      } catch (error: any) {
        testResults.push({
          test: 'Login Test',
          result: '❌ Error',
          details: error.message
        });
      }

      setDetails(testResults);
      setStatus('Tests completed');
    };

    runTests();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">🔧 RahsMatch Admin Debug</h1>

        <div className="bg-slate-900 rounded-lg p-6 border border-slate-800">
          <h2 className="text-xl font-semibold mb-4">Status: {status}</h2>

          <div className="space-y-4">
            {details.map((item, index) => (
              <div key={index} className="border border-slate-700 rounded p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">{item.test}</h3>
                  <span className="text-sm">{item.result}</span>
                </div>
                <pre className="text-sm text-slate-400 bg-slate-800 p-2 rounded overflow-auto">
                  {typeof item.details === 'string'
                    ? item.details
                    : JSON.stringify(item.details, null, 2)
                  }
                </pre>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center">
          <a
            href="/admin/login"
            className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold"
          >
            Go to Login Page
          </a>
        </div>
      </div>
    </div>
  );
};

export default TestPage;
